<template>
	<div class="Report u-color-vars" :class="isPrintLayout ? 'print' : ''">
		<div v-if="!isPrintLayout" class="Report-message">
			<ReportMessage
				:tripReportId="tripReportModelData.tripReportId"
				:tripReportOwner="this.tripReportModelData.people.filter((person) => person.role === 'owner')[0]"
				:currentUser="currentUser"
				:isWelcome="isWelcome"
			/>
		</div>
		<div v-if="currentUser.isParticipant && !isPrintLayout" class="Report-toolbar">
			<ReportToolbar />
		</div>
		<div v-if="isPrintLayout" class="Report-print">
			<ReportPrint :tripReportId="tripReportModelData.tripReportId" />
		</div>
		<div class="Report-heading">
			<ReportHeading
				:tripName="tripReportModelData.name"
				:tripBeginDt="tripReportModelData.beginDt"
				:tripEndDt="tripReportModelData.endDt"
				:privacySetting="tripReportModelData.privacy"
				:locale="locale"
				:currentUser="currentUser"
				:confirmedPeople="confirmedPeople"
				:isPrintLayout="isPrintLayout"
				:comments="comments"
				:narrative="narrative"
			/>
		</div>
		<div class="Report-meta">
			<ReportMeta
				:tripReportModelData="tripReportModelData"
				:currentUser="currentUser"
				:confirmedPeople="confirmedPeople"
				:isPrintLayout="isPrintLayout"
				:comments="comments"
				:narrative="narrative"
			/>
		</div>
		<div class="Report-main">
			<ReportStats
				:tripReportId="tripReportModelData.tripReportId"
				:selectedPersonId="selectedPersonId"
				:confirmedPeople="confirmedPeople"
				:currentView="currentSpeciesView"
				:liferFilters="liferFilters"
				@view-selected="setCurrentSpeciesView"
				@lifer-toggle="setLiferFilter"
				:isPrintLayout="isPrintLayout"
			/>
			<ReportList
				:tripReportId="tripReportModelData.tripReportId"
				:selectedPersonId="selectedPersonId"
				:currentView="currentSpeciesView"
				:currentUser="currentUser"
				:liferFilters="liferFilters"
				:locale="locale"
				:sppPref="sppPref"
				:sppLocale="sppLocale"
				:isPrintLayout="isPrintLayout"
			/>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import ReportMessage from './components/ReportMessage.vue';
import ReportToolbar from './components/ReportToolbar.vue';
import ReportPrint from './components/ReportPrint.vue';
import ReportHeading from './components/ReportHeading.vue';
import ReportMeta from './components/ReportMeta.vue';
import ReportStats from './components/ReportStats.vue';
import ReportList from './components/ReportList.vue';

export default {
	name: 'ReportApp',
	components: {
		ReportMessage,
		ReportToolbar,
		ReportPrint,
		ReportHeading,
		ReportMeta,
		ReportStats,
		ReportList,
	},
	computed: {
		// get locale by reformating java string fromfor browswer
		locale() {
			let locale = 'en-US';
			// Check for global locale variable set in JSP
			if (window.locale) {
				locale = window.locale.replace('_', '-');
			}
			// console.log(`Locale: ${locale}`);
			return locale;
		},
		currentUser() {
			let currentUser = {};
			// Set userId (from JSP)
			currentUser.userId = window.userId;
			// Set display name (from JSP)
			currentUser.userDisplayName = window.userDisplayName;
			// Set role - 'viewer' (default), 'owner', 'editor', 'participant', 'invited'
			currentUser.role = 'viewer'; // default
			const tripReportPeople = this.tripReportModelData.people;
			const tripPeopleIds = tripReportPeople.map((person) => person.userId);
			if (tripPeopleIds.indexOf(currentUser.userId) !== -1) {
				const currentParticipant = tripReportPeople.filter((person) => {
					return person.userId === currentUser.userId;
				});
				currentUser.role = currentParticipant[0].role;
				currentUser.tripReportPersonId = currentParticipant[0].tripReportPersonId;
			}
			// Set isParticipant - true if role = owner, editor, or participant
			currentUser.isParticipant = currentUser.role === 'owner' || currentUser.role === 'editor' || currentUser.role === 'participant';
			return currentUser;
		},
		// Confirmed people excludes those with roles of invited
		confirmedPeople() {
			let confirmedPeople = this.tripReportModelData.people.filter((person) => {
				return person.role !== 'invited';
			});
			return confirmedPeople;
		},
	},
	data() {
		// TODO HCW Reminder: JSON will not return key/value pair for any key with a null value.
		// We'll probably want to set them to null if they aren't there.
		return {
			tripReportModelData: window.tripReport,
			tripApiBaseUrl: window.tripApiBaseUrl,
			selectedPersonId: window.tripReportPersonId ? window.tripReportPersonId : '',
			currentSpeciesView: '',
			isWelcome: false,
			isPrintLayout: window.isPrintLayout,
			liferFilters: [],
			sppPref: window.snp,
			sppLocale: window.sppLocale,
			comments: [],
			narrative: '',
		};
	},
	mounted() {
		// handle back/forward in browser history
		window.addEventListener('popstate', this.setStateFromParams);
		// set isWelcome then remove from url
		this.setWelcomeFromParam();
		this.getComments();
		this.getNarrative();
	},
	created() {
		// set current view and lifer filter
		this.setStateFromParams();
	},
	beforeDestroy() {
		window.removeEventListener('popstate', this.setStateFromParams);
	},
	methods: {
		setCurrentSpeciesView(view) {
			this.currentSpeciesView = view;
			const url = new URL(window.location.href);
			if (view !== 'species') {
				url.searchParams.set('view', view);
			} else {
				url.searchParams.delete('view');
			}
			if (view === 'checklists') {
				// remove lifer filters for checklist view
				this.liferFilters = [];
				url.searchParams.delete('lifers');
			}
			window.history.pushState(view, '', url);
		},
		setLiferFilter(liferType) {
			if (this.currentSpeciesView === 'checklists') {
				// do nothing if in checklist view
				return;
			}
			if (this.liferFilters.includes(liferType)) {
				// disable filter
				this.liferFilters = this.liferFilters.filter((x) => x !== liferType);
			} else {
				// enable filter
				this.liferFilters.push(liferType);
			}
			// push filter to history
			const url = new URL(window.location.href);
			if (this.liferFilters.length) {
				url.searchParams.set('lifers', this.liferFilters.join(','));
			} else {
				url.searchParams.delete('lifers');
			}
			window.history.pushState('lifer', '', url);
		},
		setStateFromParams() {
			const params = new URLSearchParams(window.location.search);
			const view = params.get('view');
			if (view) {
				this.currentSpeciesView = view;
			} else {
				this.currentSpeciesView = 'species';
			}
			const lifers = params.get('lifers');
			if (lifers) {
				this.liferFilters = lifers.split(',');
			} else {
				this.liferFilters = [];
			}
		},
		setWelcomeFromParam() {
			// When welcome param is present, set isWelcome to true, then delete welcome param
			const url = new URL(window.location.href);
			const params = new URLSearchParams(url.search);
			if (params.has('welcome')) {
				this.isWelcome = true;
				params.delete('welcome');
				let paramsString = params.length > 0 ? `?${params}` : '';
				window.history.replaceState({}, '', `${location.pathname}${paramsString}`);
			}
		},
		getComments() {
			let url = `${this.tripApiBaseUrl}comments/${this.tripReportModelData.tripReportId}`;
			axios
				.get(url)
				.then((res) => {
					// api returns null if no comments
					if (res.data) {
						this.comments = res.data;
					}
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		getNarrative() {
			let url = `${this.tripApiBaseUrl}narrative/${this.tripReportModelData.tripReportId}`;
			axios
				.get(url)
				.then((res) => {
					// api returns null if no comments
					if (res.data) {
						if (!(Object.keys(res.data).length === 0)) {
							this.narrative = res.data.narrative;
						}
					}
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
	},
};
</script>

<style lang="scss">
:root {
	--color-lifer: #{$color-primary-3};
}

.Report {
	--tripReportMainWidth: min(100vw, 102rem);
	@include breakpoint(medium) {
		--tripReportAsideWidth: 40%;
		--tripReportMainWidth: 60%;
	}
	@include breakpoint(large) {
		--tripReportAsideWidth: 30%;
		--tripReportMainWidth: 70%;
	}
	background-color: $color-neutral-0;
	flex-grow: 1;
	display: grid;
	grid-template-columns: min(100vw, 102rem);
	grid-template-rows: auto auto auto auto 1fr;
	grid-template-areas:
		'message'
		'toolbar'
		'heading'
		'meta'
		'main';
	@include breakpoint(medium) {
		grid-template-columns: 40% 60%;
		grid-template-columns: var(--tripReportAsideWidth) var(--tripReportMainWidth);
		grid-template-rows: auto auto auto 1fr;
		grid-template-areas:
			'message message'
			'toolbar toolbar'
			'heading heading'
			'meta main';
	}
	.Hero {
		grid-template-columns: 30% 70%;
		grid-template-columns: var(--tripReportAsideWidth) var(--tripReportMainWidth);
	}
}
.Report-message {
	//min-height: 42px; // loading placeholder
	grid-area: message;
}
.Report-toolbar {
	min-height: 52px; // loading placeholder
	grid-area: toolbar;
	background-color: $color-primary-6;
}
.Report-heading {
	grid-area: heading;
	background-color: $color-primary-5;
	max-width: 100%;
}
.Report-meta {
	grid-area: meta;
	background-color: $color-primary-0;
}
.Report-main {
	// min-height: 0vh;
	grid-area: main;
	background-color: $color-white;
}

.Placeholder {
	background-color: $color-neutral-0;
	@include inset-md;
	border-radius: $radius-sm;
	display: flex;
	flex-direction: column;
	gap: $space-sm;
	> * {
		margin: 0;
	}
}

// Utilities to propose in base lichen
.u-stack-inside,
.u-stack-inside-md {
	display: flex;
	flex-direction: column;
	gap: $space-md;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include stack-md;
		}
	}
}
.u-stack-inside-sm {
	display: flex;
	flex-direction: column;
	gap: $space-sm;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include stack-sm;
		}
	}
}
.u-stack-inside-responsive {
	display: flex;
	flex-direction: column;
	gap: $space-sm;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include stack-sm;
		}
	}
	@include breakpoint(medium) {
		gap: $space-md;
		@supports not (gap: 1px) {
			> *:not(:last-child) {
				@include stack-md;
			}
		}
	}
}

.u-inline-inside,
.u-inline-inside-md {
	display: inline-flex;
	flex-wrap: wrap;
	gap: $space-md;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include inline-md;
		}
	}
}
.u-inline-inside-sm {
	display: inline-flex;
	flex-wrap: wrap;
	gap: $space-sm;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include inline-sm;
		}
	}
}
.u-inline-inside-xs {
	display: inline-flex;
	flex-wrap: wrap;
	gap: $space-xs;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include inline-xs;
		}
	}
}
.u-inline-inside-responsive {
	display: inline-flex;
	flex-wrap: wrap;
	gap: $space-sm;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include inline-sm;
		}
	}
	@include breakpoint(medium) {
		gap: $space-md;
		@supports not (gap: 1px) {
			> *:not(:last-child) {
				@include inline-md;
			}
		}
	}
}

.u-color-success {
	color: $color-secondary-4;
}
.u-color-alert {
	color: $color-alert-4;
}

// PRINT LAYOUT
.print {
	.Report {
		display: block;
		background-color: transparent;
	}
	.Report-heading {
		background-color: transparent;
	}
	.Report-meta {
		background-color: transparent;
	}
	.GoToTop {
		display: none !important;
	}
}
</style>
